import { useCallback, useEffect, useMemo, useState } from "react";

import { usePageSnapArtifact } from "@/features/snapping/use-page-snap-artifact";
import {
  buildSnapIndex,
  buildSnapSegmentIndex,
  type SnapIndex,
  type SnapSegmentIndex,
} from "@/features/snapping/snap-index";
import type {
  SnapPoint,
  SnapPointKind,
  SnapSegment,
} from "@/features/snapping/types";
import type { DocumentViewportValue } from "@/features/document-viewport/types";
import { calculatePixelDistance, type Point } from "@/lib/geometry";

const SNAP_DISTANCE_PX = 20;
const PAGE_SNAP_POINT_CELL_SIZE = 0.01;
const PAGE_SNAP_SEGMENT_CELL_SIZE = 0.02;
const EMPTY_SNAP_POINTS: SnapPoint<SnapPointKind>[] = [];
const EMPTY_SNAP_SEGMENTS: SnapSegment[] = [];
const SNAP_POINT_KIND_PRIORITY: Record<string, number> = {
  "object-center": 0,
  midpoint: 1,
  endpoint: 2,
  termination: 3,
  corner: 4,
  intersection: 5,
};

interface ActiveSnapMatch {
  pagePoint: Point;
  distancePx: number;
}

interface SnapLookupOptions {
  filter?: (point: Point) => boolean;
  mapPoint?: (point: Point) => Point;
  resolveSegmentPoint?: (point: Point, segment: SnapSegment) => Point | null;
}

function getSnapPointPriority(point: SnapPoint<SnapPointKind>): number {
  return SNAP_POINT_KIND_PRIORITY[point.kind] ?? 0;
}

function getPageToScreenDistanceScale(
  transforms: DocumentViewportValue["transforms"]
) {
  const origin = transforms.pageToScreenPoint({ x: 0, y: 0 });
  const xUnit = transforms.pageToScreenPoint({ x: 1, y: 0 });
  const yUnit = transforms.pageToScreenPoint({ x: 0, y: 1 });

  return {
    x: calculatePixelDistance(origin, xUnit),
    y: calculatePixelDistance(origin, yUnit),
  };
}

interface UseMeasurementOverlayPdfSnapArgs {
  viewport: DocumentViewportValue;
  snapEnabled: boolean;
}

export type PdfSnapStatus =
  | "disabled"
  | "loading"
  | "ready"
  | "empty"
  | "unavailable"
  | "error";

export function useMeasurementOverlayPdfSnap({
  viewport,
  snapEnabled,
}: UseMeasurementOverlayPdfSnapArgs) {
  const [snapHoverPoint, setSnapHoverPoint] = useState<Point | null>(null);
  const shouldLoadArtifact =
    viewport.source.mediaKind === "pdf" &&
    Boolean(viewport.source.revisionId) &&
    snapEnabled;

  const { artifact, error, isLoading } = usePageSnapArtifact(
    shouldLoadArtifact,
    null,
    viewport.activePage,
    viewport.source.revisionId
  );

  useEffect(() => {
    setSnapHoverPoint(null);
  }, [shouldLoadArtifact, viewport.activePage, viewport.source.revisionId]);

  const artifactSnapPoints = artifact?.points ?? EMPTY_SNAP_POINTS;
  const artifactSnapSegments = artifact?.segments ?? EMPTY_SNAP_SEGMENTS;
  const status: PdfSnapStatus = !shouldLoadArtifact
    ? "disabled"
    : error
      ? "error"
      : isLoading
        ? "loading"
        : artifact
          ? artifactSnapPoints.length > 0 || artifactSnapSegments.length > 0
            ? "ready"
            : "empty"
          : "unavailable";

  const pageSnapIndex = useMemo<SnapIndex<SnapPoint<SnapPointKind>> | null>(() => {
    if (artifactSnapPoints.length === 0) {
      return null;
    }

    return buildSnapIndex(artifactSnapPoints, PAGE_SNAP_POINT_CELL_SIZE);
  }, [artifactSnapPoints]);

  const pageSnapSegmentIndex = useMemo<SnapSegmentIndex | null>(() => {
    if (artifactSnapSegments.length === 0) {
      return null;
    }

    return buildSnapSegmentIndex(
      artifactSnapSegments,
      PAGE_SNAP_SEGMENT_CELL_SIZE
    );
  }, [artifactSnapSegments]);

  const snapLookupScale = useMemo(
    () => getPageToScreenDistanceScale(viewport.transforms),
    [viewport.transforms]
  );

  const findNearestSegmentSnapMatch = useCallback(
    (
      pagePoint: Point,
      options?: SnapLookupOptions
    ): ActiveSnapMatch | null => {
      if (!pageSnapSegmentIndex) {
        return null;
      }

      const match = pageSnapSegmentIndex.findNearestPoint(pagePoint, SNAP_DISTANCE_PX, {
        scale: snapLookupScale,
        filter: options?.filter,
        resolvePoint: options?.resolveSegmentPoint,
      });
      if (!match) {
        return null;
      }

      return {
        pagePoint: match.point,
        distancePx: match.distancePx,
      };
    },
    [pageSnapSegmentIndex, snapLookupScale]
  );

  const findSnapMatch = useCallback(
    (pagePoint: Point, options?: SnapLookupOptions): ActiveSnapMatch | null => {
      const pointMatch = pageSnapIndex?.findNearest(pagePoint, SNAP_DISTANCE_PX, {
        scale: snapLookupScale,
        priority: getSnapPointPriority,
        filter: options?.filter,
      });
      const normalizedPointMatch = pointMatch
        ? {
            pagePoint: options?.mapPoint
              ? options.mapPoint(pointMatch.point)
              : { x: pointMatch.point.x, y: pointMatch.point.y },
            distancePx: pointMatch.distancePx,
          }
        : null;
      const segmentMatch = findNearestSegmentSnapMatch(pagePoint, options);

      if (!normalizedPointMatch) {
        return segmentMatch;
      }
      return normalizedPointMatch;
    },
    [findNearestSegmentSnapMatch, pageSnapIndex, snapLookupScale]
  );

  const getSnappedPagePoint = useCallback(
    (pagePoint: Point, options?: SnapLookupOptions) =>
      findSnapMatch(pagePoint, options)?.pagePoint ?? pagePoint,
    [findSnapMatch]
  );

  const updateSnapHoverPoint = useCallback(
    (pagePoint: Point | null, options?: SnapLookupOptions) => {
      if (!snapEnabled || !pagePoint) {
        setSnapHoverPoint(null);
        return null;
      }

      const match = findSnapMatch(pagePoint, options);
      const nextPoint = match?.pagePoint ?? null;
      setSnapHoverPoint(nextPoint);
      return nextPoint;
    },
    [findSnapMatch, snapEnabled]
  );
  const clearSnapHoverPoint = useCallback(() => setSnapHoverPoint(null), []);

  return {
    status,
    snapHoverPoint,
    clearSnapHoverPoint,
    getSnappedPagePoint,
    updateSnapHoverPoint,
  };
}
